// Brief 85 §4 — CollaboratorModal, moved out of App.tsx.
//
// MOVED, NOT REWRITTEN. The statement block below is byte-identical to the body
// of the co clause as it stood in App.tsx —
//   sha256 9bbad87b4a606c112cb6e61a4605b01b17d21dea9257f6aa299d13ff0e7cc276
// — indentation included, which is why it sits at the depth it had inside
// App(). Only the imports, the Props type and the signature are new. Do not
// reformat: the indentation IS the proof.
//
// Props are the UNION of the App-scope names the body actually read, from a
// TypeScript-checker walk rather than chosen. The dialog declares no hook (§0
// measured zero across all 21 blocks), so it moves as a component without any
// state ownership changing hands.

import FieldSelect from "../../components/FieldSelect";
import { NO_AUTOFILL, noAutofillClass } from "../../lib/noAutofill"; // 213B §18 — no sign-ins, no contacts, in a modal's field
import { FS, RAD } from "../../lib/tokens";
import { ModalLayout } from "../../components/SharedUI";
import { PasswordField } from "../../components/PasswordField";
import { SK, canEditInternalRates, normalizeWeeklySchedule } from "../../utils";
import { api, getCollaborators } from "../../api";
import { roleDefaultRatio, roleLabel } from "../../hooks/useRoleLabels";
import { DIVIDER_CODE_PREFIX } from "../../utils/taxonomyMap"; // 154B §2
import type { TFn, TaxList } from "../../lib/taxonomyTypes";
import type { FieldSelectOption } from "../FieldSelect";
import type { makeModalFields } from "../modalFields";
import AmountField from "../AmountField"; // 208 §10 — the one amount field

type Vocab = ReturnType<typeof makeModalFields>;

type Props = {
  F: Vocab["F"];
  TitSel: Vocab["TitSel"];
  _fl: Vocab["_fl"];
  _mt: Vocab["_mt"];
  _appIsAdmin: boolean;
  _appPerms: any;
  _coCreatePwPayload: any;
  _m365Configured: boolean;
  _roleRows: readonly any[];
  _setCoCreatePw: unknown;
  barEntries: TaxList;
  barsList: readonly FieldSelectOption[];
  cm: () => void;
  coCreatePwRef: any;
  collaborators: readonly any[];
  currentUser: any;
  form: any;
  saveEntity: (k: string, rows: readonly unknown[], set: (v: readonly unknown[]) => void, f: unknown, ext?: Record<string, unknown>, opts?: { createExtra?: Record<string, unknown> }) => void;
  setCollaborators: (v: unknown) => void;
  setForm: (v: unknown) => void;
  t: TFn;
  toast: (m?: string, type?: string, opts?: { action?: unknown }) => void;
  updateField: (k: string, v: unknown) => void;
};

export default function CollaboratorModal({
  F,
  TitSel,
  _fl,
  _mt,
  _appIsAdmin,
  _appPerms,
  _coCreatePwPayload,
  _m365Configured,
  _roleRows,
  _setCoCreatePw,
  barEntries,
  barsList,
  cm,
  coCreatePwRef,
  collaborators,
  currentUser,
  form,
  saveEntity,
  setCollaborators,
  setForm,
  t,
  toast,
  updateField,
}: Props) {
        const _coAdm = !!_appIsAdmin;
        /* 04g §1 — the create default, resolved against the roles that EXIST
           rather than asserted as a literal. This was `"Associate"` in four
           places; that name is real on tenant 1 and absent from a fresh tenant,
           where P21a rules the only seeded role is `Admin`
           (seed_deploy_bootstrap_firm.js:121) — so the literal sent a create to
           a role the firm does not have. Prefer the old default where the firm
           still has it, so nothing moves for an existing firm, else the first
           role /api/roles/labels returns (ordered by id, roles.js:81). Empty
           only while the fetch is in flight or after it failed, and the select
           says so rather than offering a value the server would refuse. */
        const _defaultRoleName =
          _roleRows.find((r) => r.name === "Associate")?.name ||
          _roleRows[0]?.name ||
          "";
        /* 04g — an administrator may not clear his OWN admin flag; see the
           Administrator field below for why the guard lives here and not on
           the server. Only bites on an existing row that currently has it. */
        const _coAdmSelfLocked =
          form.id != null && form.id === currentUser && !!form.isAdmin;
        /* 75 §3 — one writer for the three states (PUT /collaborators/:id/
           status). Immediate, not part of Save; the thrown message already
           carries the srvErr-localised text (api.ts), so the last-admin 409
           and the bad-status 400 surface as they are. */
        const _setCoStatus = async (next: string) => {
          try {
            const row = (await api.put(`/collaborators/${form.id}/status`, { status: next })) as { status: string };
            updateField("status", row.status);
            setCollaborators(await getCollaborators());
            toast(
              t("co.status_changed", {
                fallback: "Account status updated",
              }),
            );
          } catch (e) {
            toast(e instanceof Error ? e.message : String(e));
          }
        };
        const ROF = (l: any, v: any) => (
          <div className="field">
            <label>{_fl(l)}</label>
            <div
              className="field-input"
              style={{
                background: "var(--bi)",
                color: "var(--ts)",
                cursor: "default",
                minHeight: "var(--field-h,30px)",
                display: "flex",
                alignItems: "center",
              }}
            >
              {v || "—"}
            </div>
          </div>
        );
        return (
          <ModalLayout
            recordId={(form as { id?: number | null }).id} // 204 §G — the person's internal id in the header (`form` is `any` here; narrowed at the site)
            title={form.id ? _mt("Edit Collaborator") : _mt("New Collaborator")}
            onClose={cm}
            onSave={() => {
              /* 04g §2 — refuse a create that would produce an account nobody
                 can use. Where Microsoft sign-in is not configured, a
                 collaborator with no password cannot log in at all, so the
                 password is required and saying nothing is the one wrong
                 answer. The ref holds only a value PasswordField has already
                 validated against the server's floor, so "" means absent or
                 not yet valid — both of which this must stop. */
              if (
                form.id == null &&
                _coAdm &&
                !_m365Configured &&
                !coCreatePwRef.current
              ) {
                toast(
                  t("toast.co.password_required", {
                    fallback:
                      "⚠ A password is required — Microsoft sign-in is not configured on this server",
                  }),
                );
                return;
              }
              /* Round 13 (C74b) — the defaults are CREATE-only. On EDIT they
                 used to manufacture changes the user never made (a stored
                 NULL defaultRatio became the role default, NULL coType
                 became "internal", …) — which is exactly what tripped the
                 server's admin-only field gate on an untouched form. An
                 edit save sends the form as-is; the server's
                 compare-and-drop ignores genuinely unchanged fields. */
              /* 164 §C.1 — the ratio draft is a string while typed (empty
                 allowed); coerce it HERE, with "" falling back to the role
                 default — never to 0. `typeof === "string"` keeps C74b's
                 edit-save semantics: an untouched form carries a number or
                 null and passes through IDENTICAL — nothing manufactured. */
              const _ratioDraft = (
                form as { defaultRatio?: number | string | null }
              ).defaultRatio;
              const _roleName =
                ((form as { role?: string }).role || _defaultRoleName) as string;
              const sendForm =
                typeof _ratioDraft === "string"
                  ? {
                      ...form,
                      defaultRatio:
                        _ratioDraft === ""
                          ? (roleDefaultRatio(_roleName) ?? 100)
                          : +_ratioDraft,
                    }
                  : form;
              const _ratioForCreate =
                typeof _ratioDraft === "string"
                  ? _ratioDraft === ""
                    ? null
                    : +_ratioDraft
                  : typeof _ratioDraft === "number"
                    ? _ratioDraft
                    : null;
              saveEntity(
                SK.co,
                collaborators,
                setCollaborators,
                sendForm,
                form.id
                  ? {}
                  : {
                      role: form.role || _defaultRoleName,
                      coType: form.coType || "internal",
                      defaultRatio:
                        _ratioForCreate ??
                        (roleDefaultRatio(_roleName) ?? 100),
                      internalRates: Array.isArray(form.internalRates)
                        ? form.internalRates
                        : [],
                      weeklySchedule: normalizeWeeklySchedule(
                        form.weeklySchedule,
                      ),
                    },
                /* Read SYNCHRONOUSLY, here, and this is load-bearing: the
                   engine's create runs inside a setState updater, which React
                   invokes AFTER this handler returns — by which time
                   saveEntity's own cm() has cleared the ref. Capturing at call
                   time is what makes the clear on close safe. */
                _coCreatePwPayload(),
              );
            }}
          >
              <>
                <div className="field-row">
                  {_coAdm ? TitSel : ROF("Title", form.title)}
                  {_coAdm
                    ? F("First Name *", "firstName", "", 1)
                    : ROF("First Name", form.firstName)}
                </div>
                <div className="field-row">
                  {_coAdm
                    ? F("Last Name *", "lastName")
                    : ROF("Last Name", form.lastName)}
                  {/* 04g §1 — THE OPTIONS COME FROM THE ROLES PLANE, not from
                      the `collaborator_role` taxonomy. The value submitted is
                      `Role.name`, which is what the server resolves against
                      (collaborators.js:72); the localised text is display only.
                      Until this, the taxonomy's LOCALISED LABEL was used as
                      both — toDisplayList's "string" shape returned
                      pickEntryLabel(e) and discarded the code (05n DELETED
                      that shape family; there is no line to repair this to) —
                      so a French UI submitted
                      "Collaborateur" and every create failed with
                      `Unknown role: Collaborateur`. It was never only a
                      translation bug: the two lists disagreed in English too
                      (offered `Admin`, `Of Counsel`, `Executive Secretary`;
                      the roles are none, `OfCounsel`, `ExecutiveSecretary`),
                      and `Paralegal`/`Reception` could not be offered at all.
                      `_roleRows` is /api/roles/labels, already loaded at :1323.
                      Same namespace on both sides now, which is also what
                      fixes value= matching an option on EDIT and what lets
                      roleDefaultRatio() below find its row. */}
                  {/* 154B §2 — FieldSelect's own shape, and the `disabled`
                      arm §1 widened MenuSelect for is exactly this one.

                      THE EMPTY-LIST LINE STAYS UNPICKABLE. It was <option
                      value="" disabled>, which a native select shows and
                      refuses; the equivalent that is not an approximation is
                      108 §6b's divider row — the one row shape this menu
                      already guarantees can never be committed. It reads the
                      same sentence between two rules instead of as a greyed
                      option, and picking it is impossible rather than merely
                      discouraged. */}
                  <FieldSelect
                    label={_fl("Role")}
                    disabled={!_coAdm}
                    value={form.role}
                    fallback={_defaultRoleName}
                    onChange={(r) =>
                      setForm((p: any) => ({
                        ...p,
                        role: r,
                        defaultRatio: p.id
                          ? p.defaultRatio
                          : (roleDefaultRatio(r) ?? 100),
                      }))
                    }
                    options={[
                      ...(_roleRows.length === 0
                        ? [
                            [
                              `${DIVIDER_CODE_PREFIX}roles_unavailable`,
                              t("collab.roles.unavailable", {
                                fallback: "Roles unavailable — reload the page",
                              }),
                            ] as const,
                          ]
                        : []),
                      ..._roleRows.map(
                        (r) => [r.name, roleLabel(r.name)] as const,
                      ),
                    ]}
                  />
                </div>
                <div className="field-row">
                  {/* C63 — max 3 chars, uppercased as typed (the server
                      normalizes trim/uppercase/slice-3 again on save).
                      Round 14 (C89, reversal of C74c) — ADMIN-ONLY now:
                      self-renaming initials is a firm-wide confusion risk,
                      so non-admins get the read-only display even on their
                      own record (server matrix matches). */}
                  {_coAdm ? (
                  <div className="field">
                    <label>{_fl("Initials")}</label>
                    <input
                      {...NO_AUTOFILL}
                      className={noAutofillClass("field-input")}
                      value={form.initials || ""}
                      maxLength={3}
                      style={{ textTransform: "uppercase" }}
                      onChange={(e) =>
                        updateField(
                          "initials",
                          e.target.value.toUpperCase().slice(0, 3),
                        )
                      }
                    />
                  </div>
                  ) : (
                    ROF("Initials", form.initials)
                  )}
                  <div className="field">
                    {/* 164 §C.2 — no <label> here: _fl("Initials note")
                        resolved to the SAME sentence as the paragraph below
                        (modalLabelKeys → modal.co.initials_note), so the
                        note printed twice, once uppercased. */}
                    <div
                      style={{
                        fontSize: FS.s9_5,
                        color: "var(--tm)",
                        lineHeight: 1.4,
                        paddingTop: 5,
                      }}
                    >
                      {t("modal.co.initials_note", {
                        fallback:
                          "Max 3 characters — shown on initials badges app-wide and in saved email-PDF filenames. Blank = derived from name.",
                      })}
                    </div>
                  </div>
                </div>
                <div className="field-row">
                  {!form._lockCoType && (
                    <div className="field">
                      <label>{_fl("Type")}</label>
                      <div style={{ display: "flex", gap: 4 }}>
                        {/* i18n-client-literals — the map variable was named
                            `t` and SHADOWED the i18n resolver inside this
                            closure (the C.1 class), so keying these two words
                            required the rename before the t() call could
                            exist. */}
                        {["internal", "external"].map((ct) => (
                          <button
                            key={ct}
                            type="button"
                            disabled={!_coAdm}
                            className={`btn ${(form.coType || "internal") === ct ? "btn-primary" : "btn-ghost"}`}
                            style={{
                              padding: "4px 12px",
                              fontSize: FS.s10,
                              flex: 1,
                              cursor: _coAdm ? "pointer" : "default",
                              opacity: _coAdm || (form.coType || "internal") === ct ? 1 : 0.45,
                            }}
                            onClick={_coAdm ? () => updateField("coType", ct) : undefined}
                          >
                            {ct === "internal"
                              ? t("type.internal", { fallback: "Internal" })
                              : t("type.external", { fallback: "External" })}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                  <div className="field">
                    <label>{_fl("Default Ratio")} (%)</label>
                    {/* 164 §C.1 — the draft is a STRING while typing (the
                        RolesAdminPanel house pattern): `+e.target.value` on
                        a cleared field wrote 0, so typing "50" yielded
                        "050". Empty is allowed here; the save site coerces
                        and falls back to the role default. */}
                    <input
                      className="field-input"
                      type="number"
                      disabled={!_coAdm}
                      min={0}
                      max={200}
                      value={(() => {
                        const dr = (
                          form as {
                            defaultRatio?: number | string | null;
                          }
                        ).defaultRatio;
                        return typeof dr === "string"
                          ? dr
                          : dr != null
                            ? dr
                            : (roleDefaultRatio(
                                ((form as { role?: string }).role ||
                                  _defaultRoleName) as string,
                              ) ?? 100);
                      })()}
                      onChange={(e) =>
                        updateField("defaultRatio", e.target.value)
                      }
                    />
                  </div>
                </div>
                {/* 04g — ADMINISTRATOR, and it is the fifth defect rather than
                    a nicety. `isAdmin` is its own Boolean column; it was never
                    a role. The only thing in the app that LOOKED like it
                    granted admin was the role dropdown's `Admin` entry — a
                    taxonomy label with no matching Role row, so it could only
                    ever 400. Dropping that entry in §1 is correct and would
                    have left NO path to administrator in the UI at all: today
                    the sole way to make one is scripts/create-admin.js, from a
                    shell. The server has accepted this field on both verbs all
                    along (ALLOWED, collaborators.js:26) and treats it as
                    admin-only (:197).

                    An admin cannot clear it on their OWN row. The PUT matrix
                    only guards non-admins (:145), so nothing server-side stops
                    the last administrator demoting himself and locking the firm
                    out of its own settings — a footgun this control would be
                    creating, not inheriting. Revoking somebody ELSE's admin is
                    unrestricted, so stepping down is asking another admin,
                    which is what lockout prevention means. */}
                <div className="field-row">
                  <div className="field">
                    <label>{_fl("Administrator")}</label>
                    <label
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 6,
                        marginTop: 5,
                        color: _coAdmSelfLocked ? "var(--tm)" : "var(--am)",
                        cursor: _coAdm && !_coAdmSelfLocked ? "pointer" : "default",
                      }}
                    >
                      <input
                        type="checkbox"
                        disabled={!_coAdm || _coAdmSelfLocked}
                        checked={!!form.isAdmin}
                        onChange={(e) => updateField("isAdmin", e.target.checked)}
                      />
                      {t("modal.co.is_admin", {
                        fallback: "Full access to settings and every case",
                      })}
                    </label>
                  </div>
                  <div className="field">
                    <div
                      style={{
                        fontSize: FS.s9_5,
                        color: "var(--tm)",
                        lineHeight: 1.4,
                        paddingTop: 5,
                      }}
                    >
                      {_coAdmSelfLocked
                        ? t("modal.co.is_admin_self", {
                            fallback:
                              "You cannot remove your own administrator rights — another administrator must do it.",
                          })
                        : t("modal.co.is_admin_note", {
                            fallback:
                              "Separate from the role. A role describes the work; this grants access.",
                          })}
                    </div>
                  </div>
                </div>
                {/* 75 §3 — the three states, admin-only, existing rows only.
                    Each action fires IMMEDIATELY through the dedicated status
                    route — never part of Save: `status` is deliberately not
                    in the server's ALLOWED list, so the generic PUT cannot
                    carry it past the last-admin guard. The roster is then
                    re-fetched so pickers and greying follow at once. Suspend
                    keeps the person out and changes nothing else; archive
                    also removes them from every picker. The server refuses
                    to leave the firm without an active admin. */}
                {_coAdm && form.id != null && (
                  <div className="field-row">
                    <div className="field">
                      <label>{_fl("Account")}</label>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 8,
                          marginTop: 5,
                          flexWrap: "wrap",
                        }}
                      >
                        <span
                          style={{
                            fontSize: FS.s9_5,
                            fontWeight: 600,
                            color:
                              (form.status || "active") === "active"
                                ? "var(--am)"
                                : "var(--tm)",
                          }}
                        >
                          {t("co.status." + (form.status || "active"), {
                            fallback:
                              (form.status || "active") === "suspended"
                                ? "Suspended"
                                : (form.status || "active") === "archived"
                                  ? "Archived"
                                  : "Active",
                          })}
                        </span>
                        {(form.status || "active") === "active" && (
                          <button type="button" className="btn btn-ghost" onClick={() => _setCoStatus("suspended")}>
                            {t("co.action.suspend", { fallback: "Suspend" })}
                          </button>
                        )}
                        {(form.status || "active") === "suspended" && (
                          <button type="button" className="btn btn-ghost" onClick={() => _setCoStatus("active")}>
                            {t("co.action.reinstate", { fallback: "Reinstate" })}
                          </button>
                        )}
                        {(form.status || "active") !== "archived" && (
                          <button type="button" className="btn btn-ghost" onClick={() => _setCoStatus("archived")}>
                            {t("co.action.archive", { fallback: "Archive" })}
                          </button>
                        )}
                        {(form.status || "active") === "archived" && (
                          <button type="button" className="btn btn-ghost" onClick={() => _setCoStatus("active")}>
                            {t("co.action.restore", { fallback: "Restore" })}
                          </button>
                        )}
                      </div>
                      <div
                        style={{
                          fontSize: FS.s9_5,
                          color: "var(--tm)",
                          lineHeight: 1.4,
                          marginTop: 4,
                        }}
                      >
                        {t("co.status_note", {
                          fallback:
                            "Suspend blocks sign-in and ends the running session, changing nothing else. Archive also removes them from every picker; existing records keep their name.",
                        })}
                      </div>
                    </div>
                  </div>
                )}
                <div className="field-row">
                  {_coAdm ? F("Email", "email") : ROF("Email", form.email)}
                  {F("Phone", "phone")}
                </div>
                {/* 176 §4.4 [RULED — Laurent, 2026-09-10] — SAY IT BEFORE THE
                    SAVE, NOT AFTER. A collaborator created with no e-mail is
                    filed as archived (routes/collaborators.js, the create route
                    only): absent from every picker, still shown wherever it is
                    already named, still matched by name at Excel import. That is
                    the right outcome for a record that exists to carry
                    attribution — but a person who saves and then cannot find
                    their new colleague in a dropdown has been surprised by us.

                    ON CREATE ONLY (`form.id == null`), because the rule is
                    create-only: clearing the e-mail on an existing collaborator
                    does NOT archive them, and a note here on an edit would be a
                    plain lie about what Save is about to do.

                    A note, not a warning: `var(--tm)`, no icon, no colour that
                    reads as an error. Nothing is wrong — this is what the user
                    asked for, said out loud. */}
                {form.id == null && !String(form.email || "").trim() && (
                  <div
                    style={{
                      fontSize: FS.s10,
                      color: "var(--tm)",
                      margin: "-4px 0 8px",
                      lineHeight: 1.45,
                    }}
                  >
                    {t("co.no_email_archived_note", {
                      fallback:
                        "No e-mail address: this person will be filed as archived. They will still appear on time entries and will still be matched by name on Excel imports, but they will not be offered in pickers.",
                    })}
                  </div>
                )}
                {/* P21-zero — the password flow, self-service and admin alike.
                    Rendered only on an EXISTING row (there is nothing to PUT
                    to before the collaborator exists — creation carries its
                    own password through POST) and only where the server would
                    accept the write anyway: requireSelfOrAdmin. Showing it to
                    anyone else would be an affordance that can only earn a
                    403. The plaintext lives in PasswordField's own state and
                    never enters `form`, so no ordinary save carries it. */}
                {form.id != null && (_coAdm || form.id === currentUser) && (
                  <PasswordField
                    collaboratorId={form.id}
                    isSelf={form.id === currentUser}
                    t={t}
                    toast={toast}
                  />
                )}
                {/* 04g §2 — the create half of the same contract. The comment
                    above described a design that was only half built: POST has
                    accepted a password all along and no client sent one, so
                    every collaborator created from this modal was unusable on
                    a box without Microsoft sign-in. Admin-only because POST
                    itself is requireAdmin (collaborators.js:114). The value
                    goes to a ref, never to `form`. */}
                {form.id == null && _coAdm && (
                  <PasswordField
                    mode="create"
                    required={!_m365Configured}
                    onValue={_setCoCreatePw}
                    t={t}
                    toast={toast}
                  />
                )}
                <div className="field-row">
                  <FieldSelect
                    label={_fl("Bar")}
                    disabled={!_coAdm}
                    value={form.barAssociation}
                    onChange={(v) => updateField("barAssociation", v)}
                    options={[
                      ["", `— ${t("common.none", { fallback: "None" })} —`],
                      ...barsList,
                    ]}
                    entries={barEntries}
                    t={t}
                  />
                </div>
                {canEditInternalRates(_appPerms) &&
                  (() => {
                    const raw = Array.isArray(form.internalRates)
                      ? form.internalRates
                      : [];
                    const indexed = raw
                      .map((r: any, i: any) => ({ ...r, _idx: i }))
                      .sort((a: any, b: any) =>
                        (b.startDate || "").localeCompare(a.startDate || ""),
                      );
                    const updateRow = (idx: any, key: any, val: any) => {
                      const next = raw.map((r: any, i: any) =>
                        i === idx ? { ...r, [key]: val } : r,
                      );
                      updateField("internalRates", next);
                    };
                    const addRate = () => {
                      const today = new Date().toISOString().slice(0, 10);
                      updateField("internalRates", [
                        ...raw,
                        { startDate: today, rate: "" },
                      ]);
                    };
                    return (
                      <div
                        style={{
                          borderTop: "1px solid var(--br)",
                          marginTop: 10,
                          paddingTop: 10,
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 8,
                            marginBottom: 6,
                          }}
                        >
                          <label
                            style={{
                              fontSize: FS.s11,
                              fontWeight: 600,
                              color: "var(--tp)",
                              flex: 1,
                            }}
                          >
                            {t("co.rates.title", {
                              fallback: "Internal Rates",
                            })}
                          </label>
                          <span style={{ fontSize: FS.s9, color: "var(--tm)" }}>
                            {t("co.rates.admin_only", {
                              fallback: "Admin / Partner only",
                            })}
                          </span>
                          <button
                            type="button"
                            className="btn btn-primary"
                            style={{
                              padding: "3px 10px",
                              fontSize: FS.s10,
                              whiteSpace: "nowrap",
                            }}
                            onClick={addRate}
                          >
                            {t("co.rates.add_btn", { fallback: "+ Add Rate" })}
                          </button>
                        </div>
                        <p
                          style={{
                            fontSize: FS.s10,
                            color: "var(--tm)",
                            marginBottom: 8,
                            lineHeight: 1.5,
                          }}
                        >
                          {t("co.rates.helper", {
                            fallback:
                              "Cost rate (EUR/h) used when computing costs. Previous rates are preserved as history; the most recent start date on or before a time entry date applies.",
                          })}
                        </p>
                        {indexed.length === 0 && (
                          <p
                            style={{
                              fontSize: FS.s10_5,
                              color: "var(--tm)",
                              fontStyle: "italic",
                              padding: "6px 0",
                            }}
                          >
                            {t("co.rates.empty", {
                              fallback:
                                'No rates yet. Click "+ Add Rate" to record the first rate.',
                            })}
                          </p>
                        )}
                        {indexed.length > 0 && (
                          <div
                            style={{
                              display: "grid",
                              gridTemplateColumns: "1fr 1fr auto",
                              gap: 4,
                              alignItems: "center",
                            }}
                          >
                            <span
                              style={{
                                fontSize: FS.s9,
                                color: "var(--tm)",
                                textTransform: "uppercase",
                                letterSpacing: 0.4,
                              }}
                            >
                              {t("co.rates.col_start_date", {
                                fallback: "Start Date",
                              })}
                            </span>
                            <span
                              style={{
                                fontSize: FS.s9,
                                color: "var(--tm)",
                                textTransform: "uppercase",
                                letterSpacing: 0.4,
                              }}
                            >
                              {t("co.rates.col_internal_rate", {
                                fallback: "Internal Rate (EUR/h)",
                              })}
                            </span>
                            <span />
                            {indexed.map((r: any, displayIdx: any) => (
                              <div key={r._idx} style={{ display: "contents" }}>
                                <input
                                  className="field-input"
                                  type="date"
                                  style={{ padding: "3px 6px", fontSize: FS.s11 }}
                                  value={r.startDate || ""}
                                  onChange={(e) =>
                                    updateRow(
                                      r._idx,
                                      "startDate",
                                      e.target.value,
                                    )
                                  }
                                />
                                {/* 208 §10 — the one amount field (src/components/AmountField.tsx): typed with a decimal comma, shown « 1 234,50 » */}
                                <AmountField
                                  placeholder="0,00"
                                  style={{
                                    padding: "3px 6px",
                                    fontSize: FS.s11,
                                    fontFamily: "'JetBrains Mono',monospace",
                                  }}
                                  value={r.rate}
                                  empty=""
                                  inputMode="decimal"
                                  onAmount={(n) =>
                                    updateRow(r._idx, "rate", n == null ? "" : n)
                                  }
                                />
                                {displayIdx === 0 ? (
                                  <span
                                    style={{
                                      fontSize: FS.s8,
                                      padding: "1px 5px",
                                      borderRadius: RAD.r6,
                                      background: "var(--as)",
                                      color: "var(--ac)",
                                      fontWeight: 600,
                                      whiteSpace: "nowrap",
                                    }}
                                  >
                                    {t("list.rates.current", {
                                      fallback: "CURRENT",
                                    })}
                                  </span>
                                ) : (
                                  <span />
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })()}
              </>
          </ModalLayout>
        );
}
